import * as React from 'react';
import {
  ThemeProvider as MuiThemeProvider,
  createTheme,
  responsiveFontSizes,
} from '@mui/material/styles';
import { createContext, useContext, useMemo } from 'react';
import { grey, pink, purple } from '@mui/material/colors';
import { useMediaQuery } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

export type ThemeType = 'light' | 'dark';
export const LIGHT_CONTRAST_TEXT = grey[700];
export const DARK_CONTRAST_TEXT = grey[100];

type ThemeState = {
  theme: ThemeType;
  setTheme(theme: ThemeType): void;
};

const StateContext = createContext<ThemeState>({
  theme: 'light',
  setTheme: () => {}
});

export const ThemeProvider = (props: { children: React.ReactNode }) => {
  const [theme, setTheme] = React.useState(
    (localStorage.getItem('theme') === 'dark' ? 'dark' : 'light') as ThemeType
  );

  const isMobile = useMediaQuery('(min-device-width: 300px) and (max-device-width: 1200px)');

  const darkTheme = useMemo(() => createTheme({
    palette: {
      primary: {
        main: purple[600],
        light: purple[500],
        dark: purple[700],
        contrastText: DARK_CONTRAST_TEXT,
      }
    },
    typography: {
      fontFamily: '"Montserrat", "Helvetica", "Arial", sans-serif',
      fontSize: isMobile ? 18 : 12
    },
  }), [isMobile]);

  const lightTheme = useMemo(() => createTheme({
    palette: {
      primary: {
        main: pink[50],
        light: pink[50],
        dark: pink[100],
        contrastText: LIGHT_CONTRAST_TEXT,
      },
    },
    typography: {
      fontFamily: '"Montserrat", "Helvetica", "Arial", sans-serif',
      fontSize: isMobile ? 18 : 12
    },
  }), [isMobile]);

  return (
    <StateContext.Provider
      value={{
        theme,
        setTheme,
      }}
    >
      <MuiThemeProvider
        theme={
          theme === 'light'
            ? responsiveFontSizes(lightTheme)
            : responsiveFontSizes(darkTheme)
        }
      >
        <LocalizationProvider dateAdapter={AdapterMoment}>
          {props.children}
        </LocalizationProvider>
      </MuiThemeProvider>
    </StateContext.Provider>
  );
};

export const useThemeState = () => useContext(StateContext);
